import React from "react";
import { Link } from "react-router-dom";

const supportEmail = "support@integritysuitecrm.com";

function PublicPage({ title, updated, children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#080808",
        color: "#ffffff",
        fontFamily:
          'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <header
        style={{
          borderBottom: "1px solid rgba(212,175,55,0.2)",
          background: "#050505",
        }}
      >
        <div
          style={{
            maxWidth: "960px",
            margin: "0 auto",
            padding: "20px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "20px",
            flexWrap: "wrap",
          }}
        >
          <Link
            to="/"
            style={{
              color: "#d4af37",
              textDecoration: "none",
              fontWeight: 900,
              fontSize: "20px",
            }}
          >
            Integrity Suite CRM
          </Link>

          <Link
            to="/"
            style={{
              color: "#ffffff",
              textDecoration: "none",
              fontWeight: 700,
            }}
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      <main
        style={{
          maxWidth: "960px",
          margin: "0 auto",
          padding: "70px 24px 100px",
        }}
      >
        <h1
          style={{
            fontSize: "clamp(38px, 6vw, 58px)",
            margin: "0 0 10px",
            letterSpacing: "-0.04em",
          }}
        >
          {title}
        </h1>

        {updated && (
          <p
            style={{
              color: "#999",
              marginBottom: "42px",
            }}
          >
            Last updated: {updated}
          </p>
        )}

        <div
          style={{
            color: "#d0d0d0",
            lineHeight: 1.8,
            fontSize: "17px",
          }}
        >
          {children}
        </div>
      </main>
    </div>
  );
}

const Heading = ({ children }) => (
  <h2
    style={{
      color: "#ffffff",
      fontSize: "24px",
      marginTop: "38px",
      marginBottom: "12px",
    }}
  >
    {children}
  </h2>
);

export function PrivacyPolicy() {
  return (
    <PublicPage title="Privacy Policy" updated="September 10, 2026">
      <p>
        Integrity Suite CRM provides software tools for credit repair
        businesses to manage clients, credit-report workflows, disputes,
        letters, documents, tasks, and related business operations.
      </p>

      <Heading>Information We Collect</Heading>

      <p>
        We may collect account registration information, business contact
        information, subscription and billing information, customer-support
        communications, and information entered or uploaded by authorized
        users while using the platform.
      </p>

      <p>
        Depending on how the platform is used, customer accounts may contain
        sensitive consumer information. Users are responsible for ensuring
        they have appropriate authorization to upload and process information
        relating to their clients.
      </p>

      <Heading>How We Use Information</Heading>

      <p>
        We use information to provide and operate the service, authenticate
        users, process subscriptions, maintain account security, provide
        customer support, improve platform functionality, and meet applicable
        legal or regulatory obligations.
      </p>

      <Heading>Payment Processing</Heading>

      <p>
        Subscription payments are processed by third-party payment providers
        such as Stripe. Integrity Suite CRM does not store full payment-card
        numbers on its application servers.
      </p>

      <Heading>Service Providers</Heading>

      <p>
        We may use hosting, payment-processing, infrastructure, analytics, and
        other technology providers to operate the service. Information may be
        shared with those providers only as reasonably necessary to provide
        their services to us.
      </p>

      <Heading>Data Security</Heading>

      <p>
        We use administrative, technical, and operational safeguards designed
        to protect account information. No method of electronic storage or
        transmission can be guaranteed to be completely secure.
      </p>

      <Heading>Data Retention</Heading>

      <p>
        Information may be retained for as long as reasonably necessary to
        provide the service, maintain legitimate business records, resolve
        disputes, enforce agreements, and comply with applicable legal
        requirements.
      </p>

      <Heading>Your Choices</Heading>

      <p>
        Account holders may contact us regarding questions about their account,
        privacy, or available options for updating or deleting information,
        subject to legal, security, and record-retention requirements.
      </p>

      <Heading>Contact</Heading>

      <p>
        Privacy questions may be sent to{" "}
        <a href={`mailto:${supportEmail}`} style={{ color: "#d4af37" }}>
          {supportEmail}
        </a>
        .
      </p>
    </PublicPage>
  );
}

export function TermsOfService() {
  return (
    <PublicPage title="Terms of Service" updated="September 30, 2026">
      <p>
        These Terms of Service govern access to and use of Integrity Suite CRM.
        By creating an account or using the service, you agree to these terms.
      </p>

      <Heading>Software Service</Heading>

      <p>
        Integrity Suite CRM is a software platform designed to help businesses
        organize credit repair workflows, clients, disputes, letters,
        documents, tasks, credit-report information, and related operations.
      </p>

      <Heading>No Guaranteed Credit Outcome</Heading>

      <p>
        Integrity Suite CRM does not guarantee the deletion of any credit
        account, improvement of any credit score, approval for credit, or any
        particular financial result. Users remain responsible for reviewing
        information and determining the actions appropriate for their clients.
      </p>

      <Heading>User Responsibilities</Heading>

      <p>
        Users must provide accurate account information, maintain the
        confidentiality of login credentials, use the platform lawfully, and
        obtain any authorization or consent required to process information
        belonging to their clients.
      </p>

      <p>
        Users are responsible for complying with laws and regulations
        applicable to their own businesses and services.
      </p>

      <Heading>7-Day Free Trial</Heading>
      <p>Each new company can start one 7-day free trial of its selected plan after completing checkout with a payment method. The trial end date and monthly price are shown at checkout. Unless canceled before that date, the subscription automatically becomes paid and renews monthly at the selected plan price. Cancel in Settings &gt; Billing before the trial ends to avoid the first charge. Returning subscribers do not receive a new trial.</p>
      <Heading>Subscriptions</Heading>

      <p>
        Paid plans are billed according to the price and billing interval
        presented at checkout. Subscription access may depend on successful
        payment and the selected service plan.
      </p>

      <Heading>Prohibited Use</Heading>

      <p>
        Users may not use the service for unlawful activity, unauthorized
        access, fraud, abuse, interference with platform security, or the
        unauthorized collection or processing of another person's
        information.
      </p>

      <Heading>Availability and Changes</Heading>

      <p>
        We may update, improve, modify, or discontinue features as the service
        evolves. We may also perform maintenance or take reasonable steps
        necessary to protect the security and reliability of the platform.
      </p>

      <Heading>Account Suspension or Termination</Heading>

      <p>
        Access may be suspended or terminated for nonpayment, unlawful use,
        abuse of the service, security threats, or material violations of
        these terms.
      </p>

      <Heading>Limitation</Heading>

      <p>
        The service is provided as a software tool. To the extent permitted by
        applicable law, Integrity Suite CRM is not responsible for indirect,
        incidental, special, or consequential losses resulting from use of the
        service.
      </p>

      <Heading>Contact</Heading>

      <p>
        Questions regarding these terms may be sent to{" "}
        <a href={`mailto:${supportEmail}`} style={{ color: "#d4af37" }}>
          {supportEmail}
        </a>
        .
      </p>
    </PublicPage>
  );
}

export function RefundPolicy() {
  return (
    <PublicPage
      title="Cancellation & Refund Policy"
      updated="September 30, 2026"
    >
      <Heading>Subscription Cancellation</Heading>

      <p>
        Customers can schedule cancellation in Settings &gt; Billing to prevent
        future recurring charges. Cancellation does not automatically erase
        business records or account information that must be retained for
        legitimate business, security, or legal purposes.
      </p>

      <Heading>Trial Cancellation</Heading>
      <p>Cancel before your trial ends to avoid the first subscription charge. Access continues until the scheduled trial end. Canceling after paid billing begins stops future renewals; access continues through the paid period.</p>
      <Heading>Billing Periods</Heading>

      <p>
        Subscriptions are billed in advance for the billing period shown at
        checkout. Customers should cancel before their next renewal date if
        they do not want the subscription to renew.
      </p>

      <Heading>Refunds</Heading>

      <p>
        Subscription charges are generally non-refundable once a billing
        period has begun, except where required by applicable law or where
        Integrity Suite CRM determines that a billing error occurred.
      </p>

      <p>
        We may review exceptional refund requests individually. Approval of
        one refund does not create an obligation to approve future requests.
      </p>

      <Heading>Billing Questions</Heading>

      <p>
        If you believe you were charged incorrectly or need help with a
        subscription, contact us at{" "}
        <a href={`mailto:${supportEmail}`} style={{ color: "#d4af37" }}>
          {supportEmail}
        </a>
        .
      </p>
    </PublicPage>
  );
}

export function ContactPage() {
  return (
    <PublicPage title="Contact Integrity Suite CRM">
      <p>
        Have a question about Integrity Suite CRM, your subscription, or your
        account? Contact our support team.
      </p>

      <div
        style={{
          marginTop: "34px",
          padding: "28px",
          borderRadius: "18px",
          border: "1px solid rgba(212,175,55,0.25)",
          background: "rgba(212,175,55,0.06)",
        }}
      >
        <div
          style={{
            color: "#d4af37",
            fontWeight: 900,
            marginBottom: "8px",
          }}
        >
          Email Support
        </div>

        <a
          href={`mailto:${supportEmail}`}
          style={{
            color: "#ffffff",
            fontSize: "20px",
            fontWeight: 800,
          }}
        >
          {supportEmail}
        </a>
      </div>


      <div style={{ marginTop: "24px", overflowWrap: "anywhere" }}>
        <p>
          <strong>Phone: </strong>
          <a href="tel:+19096195191" style={{ color: "#d4af37" }}>
            (909) 619-5191
          </a>
        </p>
        <p>
          <strong>Website: </strong>
          <a href="https://www.integritysuitecrm.com" style={{ color: "#d4af37" }}>
            www.integritysuitecrm.com
          </a>
        </p>
      </div>

      <Heading>About Integrity Suite CRM</Heading>

      <p>
        Integrity Suite CRM is a subscription software platform built to help
        credit repair businesses organize client management, credit-report
        review, dispute workflows, letters, case information, documents,
        tasks, and team operations.
      </p>
    </PublicPage>
  );
}
